import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import * as accountServices from "../../../contexts/services/admin/accountServices";
import { formatUserType, safeDate, safeDateTime } from "../../utils";
import GenericDialog from "../../library/Dialog";
import SavedViewsMenu from "./SavedViewMenu";
import AccountsFilters from "./AccountsFilters";
import AccountPromoCell from "./AccountPromoCell";
import { getAccountPromos } from "./accountPromoUtils";
import SecurityGuard from "../../guards/SecurityGuard";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import GoogleIcon from "@mui/icons-material/Google";
import PasswordIcon from "@mui/icons-material/Password";
import EmailIcon from "@mui/icons-material/Email";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useGuardRequest } from "../../context/useGuardRequest";
import ReasonDialog from "../../library/ReasonDialog";
import ProcessingDialog from "../../library/ProcessingDialog";
import dayjs from "dayjs";
import phoneCodes from "../../../utils/static/phoneCodes";

const STORAGE_KEY_VIEWS = "admin-accounts-saved-filter-views";

const USER_TYPE_KEYS = ["admin", "user", "subsidiary", "guest"];
const USER_TYPE_OPTIONS = USER_TYPE_KEYS.map((key) => ({
  value: key,
  label: formatUserType(key),
}));

const USER_ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

const PHONE_CODE_OPTIONS = phoneCodes.map(({ code, country }) => ({
  value: code,
  label: `${country} ${code}`,
}));

const STATUS_CHIP = {
  active: { label: "Active", color: "success" },
  inactive: { label: "Inactive", color: "default" },
  pending: { label: "Pending", color: "warning" },
  banned: { label: "Banned", color: "error" },
};

const USER_STATUS_OPTIONS = Object.entries(STATUS_CHIP).map(
  ([value, config]) => ({
    value,
    label: config.label,
  })
);

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const RISK_ACTION_LIMIT = 5;
const PASSWORD_CHARSETS = [
  "ABCDEFGHJKLMNPQRSTUVWXYZ",
  "abcdefghijkmnopqrstuvwxyz",
  "23456789",
  "!@#$%^&*",
];

const getRandomIndex = (max) => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] % max;
  }
  return Math.floor(Math.random() * max);
};

const generateInitialPassword = (length = 14) => {
  const allCharacters = PASSWORD_CHARSETS.join("");
  const password = PASSWORD_CHARSETS.map(
    (characters) => characters[getRandomIndex(characters.length)]
  );

  while (password.length < length) {
    password.push(allCharacters[getRandomIndex(allCharacters.length)]);
  }

  for (let i = password.length - 1; i > 0; i -= 1) {
    const j = getRandomIndex(i + 1);
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join("");
};

const selectMenuProps = {
  PaperProps: {
    sx: { maxHeight: 320 },
  },
};

const paperBorderSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
};

const accountsHeadCellSx = {
  fontWeight: 600,
  color: "text.secondary",
  borderBottom: "2px solid",
  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
  py: 2,
};

const accountsSortLabelSx = {
  color: "inherit",
  fontWeight: 600,
  "& .MuiTableSortLabel-icon": {
    color: "text.secondary !important",
  },
};

const accountsBodyCellSx = {
  py: 2,
  borderColor: (theme) => alpha(theme.palette.divider, 0.08),
};

const stickyColBaseSx = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  bgcolor: "background.paper",
  backgroundImage: "none",
};

const stickyColSecondSx = {
  position: "sticky",
  left: 52,
  zIndex: 2,
  bgcolor: "background.paper",
  backgroundImage: "none",
};

const defaultFilters = () => ({
  email: "",
  name: "",
  phone: "",
  user_type: "",
  user_role: "",
  status: "",
  subscription_status: "",
  hitpay_verified: "all", // "all" | "true" | "false"
  has_promo: "all",
  promo_code: "",
});

function sortRows(rows, orderBy, order) {
  const mul = order === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let va;
    let vb;
    switch (orderBy) {
      case "name":
        va = (a.name || "").toLowerCase();
        vb = (b.name || "").toLowerCase();
        return va.localeCompare(vb) * mul;
      case "email":
        va = (a.email || "").toLowerCase();
        vb = (b.email || "").toLowerCase();
        return va.localeCompare(vb) * mul;
      case "created_time":
        va = new Date(a.created_time || 0).getTime();
        vb = new Date(b.created_time || 0).getTime();
        return (va - vb) * mul;
      case "status":
        return (
          (a.user_auth?.status || "").localeCompare(b.user_auth?.status || "") *
          mul
        );
      case "user_type":
        return (
          (a.user_auth?.user_type || "").localeCompare(
            b.user_auth?.user_type || ""
          ) * mul
        );
      case "user_role":
        return (
          (a.user_auth?.user_role || "").localeCompare(
            b.user_auth?.user_role || ""
          ) * mul
        );
      default:
        return 0;
    }
  });
}

function rowsToCsv(rows) {
  const headers = [
    "u_id",
    "name",
    "email",
    "phone",
    "created_time",
    "auth_status",
    "user_type",
    "user_role",
    "plan_name",
    "subscription_status",
    "next_billing_date",
    "trial_end",
    "verified",
    "last_login_at",
    "has_promo",
    "promo_partner",
    "promo_code",
    "promo_discount_percent",
    "promo_redeemed_at",
    "pending_promo_code",
    "custom_discount_percent",
  ];
  const lines = [headers.join(",")];
  rows.forEach((r) => {
    const phone = [r.phone_code, r.phone_number].filter(Boolean).join(" ");
    const subStatus = r.subscription?.status || "";
    const promos = getAccountPromos(r);
    const row = {
      u_id: r.u_id ?? "",
      name: r.name ?? "",
      email: r.email ?? "",
      phone,
      created_time: r.created_time ?? "",
      auth_status: r.user_auth?.status ?? "",
      user_type: r.user_auth?.user_type ?? "",
      user_role: r.user_auth?.user_role ?? "",
      plan_name: r.subscription?.plan?.name ?? "",
      subscription_status: subStatus,
      next_billing_date: r.subscription?.next_billing_date ?? "",
      trial_end:
        subStatus === "trial" ? (r.subscription?.trial_end ?? "") : "NA",
      verified: r.subscription?.verified === true ? "true" : "false",
      last_login_at: r.user_auth?.last_login_at ?? "",
      has_promo: promos.length > 0 ? "true" : "false",
      promo_partner: promos.map((promo) => promo.partner_name ?? "").join("; "),
      promo_code: promos.map((promo) => promo.promo_code ?? "").join("; "),
      promo_discount_percent: promos
        .map((promo) =>
          promo.discount_type === "PERCENTAGE"
            ? (promo.discount_value ?? "")
            : ""
        )
        .join("; "),
      promo_redeemed_at: promos
        .map((promo) => promo.redeemed_at ?? "")
        .join("; "),
      pending_promo_code: r.pending_promo_code ?? "",
      custom_discount_percent: r.subscription?.custom_discount_percent ?? "",
    };
    lines.push(
      headers
        .map((h) => {
          const v = row[h] ?? "";
          const esc = String(v).replace(/"/g, '""');
          return `"${esc}"`;
        })
        .join(",")
    );
  });
  return lines.join("\n");
}

function orgsToCsv(rows) {
  const headers = [
    "organization_id",
    "organization_name",
    "created_at",

    "owner_id",
    "owner_name",
    "owner_email",

    "total_users",
    "total_companies",

    "subscription_id",
    "plan_name",
    "subscription_status",
    "verified",
    "renewal",

    "start_date",
    "end_date",
    "trial_end",
    "next_billing_date",
    "next_payment_cents",
  ];

  const lines = [headers.join(",")];

  rows.forEach((r) => {
    const sub = r.subscription || {};
    const plan = sub.plan || {};
    const owner = r.owner || {};

    const row = {
      organization_id: r.organization_id ?? "",
      organization_name: r.organization_name ?? "",
      created_at: r.created_at ?? "",

      owner_id: owner.u_id ?? "",
      owner_name: owner.name ?? "",
      owner_email: owner.email ?? "",

      total_users: r.total_users ?? 0,
      total_companies: r.total_companies ?? 0,

      subscription_id: sub.id ?? "",
      plan_name: plan.name ?? "",
      subscription_status: sub.status ?? "",
      verified: sub.verified === true ? "true" : "false",
      renewal: sub.renewal === true ? "true" : "false",

      start_date: sub.start_date ?? "NA",
      end_date: sub.end_date ?? "NA",
      trial_end: sub.status === "trialing" ? (sub.trial_end ?? "") : "NA",
      next_billing_date: sub.next_billing_date ?? "NA",
      next_payment_cents: sub.next_payment_cents ?? "0",
    };

    lines.push(
      headers
        .map((h) => {
          const v = row[h] ?? "";
          const esc = String(v).replace(/"/g, '""');
          return `"${esc}"`;
        })
        .join(",")
    );
  });

  return lines.join("\n");
}

const AccountsList = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);
  const [orderBy, setOrderBy] = useState("created_time");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState(new Set());
  const [savedViews, setSavedViews] = useState([]);
  const [saveViewAnchor, setSaveViewAnchor] = useState(null);
  const [saveViewName, setSaveViewName] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonDialogTitle, setReasonDialogTitle] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [reasonActionType, setReasonActionType] = useState("");
  const [deleteReasonDialogOpen, setDeleteReasonDialogOpen] = useState(false);
  const [deleteReasonInput, setDeleteReasonInput] = useState("");
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetPasswordReason, setResetPasswordReason] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteAdminRow, setInviteAdminRow] = useState(null);
  const [inviteKind, setInviteKind] = useState("subsidiary"); // "subsidiary" | "sub-user"
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePhoneCode, setInvitePhoneCode] = useState("+65");
  const [invitePhoneNumber, setInvitePhoneNumber] = useState("");
  const [inviteAddress, setInviteAddress] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [inviteExpireDate, setInviteExpireDate] = useState("");
  const [inviteReason, setInviteReason] = useState("");
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertTargetRow, setConvertTargetRow] = useState(null);
  const [convertReason, setConvertReason] = useState("");
  const [convertExpireDays, setConvertExpireDays] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [snack, setSnack] = useState(null);

  const updateFilters = useCallback((updater) => {
    setPage(0);
    setFilters((current) =>
      typeof updater === "function" ? updater(current) : updater
    );
  }, []);

  const handleGenerateInvitePassword = useCallback(() => {
    setInvitePassword(generateInitialPassword());
  }, []);

  const { guardProps, runActionWithMiddleware } = useGuardRequest({
    policy: {
      deleteAccount: true,
      resendVerification: true,
      reset2fa: true,
      resetPassword: true,
      convertAccountType: true,
    },
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_VIEWS);
      if (raw) setSavedViews(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (filters.email?.trim()) params.email = filters.email.trim();
      if (filters.name?.trim()) params.name = filters.name.trim();
      if (filters.phone?.trim()) params.phone = filters.phone.trim();
      if (filters.user_type) params.user_type = filters.user_type;
      if (filters.user_role) params.user_role = filters.user_role;
      if (filters.status) params.status = filters.status;
      const subscriptionStatus =
        filters.subscription_status === "trial"
          ? "trialing"
          : filters.subscription_status;
      if (subscriptionStatus) params.subscription_status = subscriptionStatus;
      if (filters.hitpay_verified === "true") params.hitpay_verified = true;
      if (filters.hitpay_verified === "false") params.hitpay_verified = false;
      if (filters.has_promo === "true") params.has_promo = true;
      if (filters.has_promo === "false") params.has_promo = false;
      if (filters.promo_code?.trim())
        params.promo_code = filters.promo_code.trim();

      const res = await accountServices.listAccounts(params);
      const data = res?.data ?? res;

      const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
      setRows(accounts);
      setTotal(Number(data?.total ?? 0));
      setSelected(new Set());
    } catch (e) {
      setSnack({ message: "Failed to load accounts" });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchAccounts();
    }, 350);
    return () => clearTimeout(t);
  }, [fetchAccounts]);

  const persistViews = useCallback((views) => {
    setSavedViews(views);
    localStorage.setItem(STORAGE_KEY_VIEWS, JSON.stringify(views));
  }, []);

  const filteredSorted = useMemo(() => {
    return sortRows(rows, orderBy, order);
  }, [rows, orderBy, order]);

  const pageRows = filteredSorted;

  const handleSort = (id) => {
    const isAsc = orderBy === id && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(id);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(new Set(pageRows.map((r) => r.u_id)));
    } else {
      setSelected(new Set());
    }
  };

  const toggleOne = (u_id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(u_id)) next.delete(u_id);
      else next.add(u_id);
      return next;
    });
  };

  const selectedCount = selected.size;
  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.u_id)),
    [rows, selected]
  );
  const pageSelectedCount = pageRows.filter((r) => selected.has(r.u_id)).length;
  const allPageSelected =
    pageRows.length > 0 && pageSelectedCount === pageRows.length;
  const somePageSelected =
    pageSelectedCount > 0 && pageSelectedCount < pageRows.length;

  const handleRefresh = () => {
    fetchAccounts();
  };

  const copyTxt = (txt) => {
    navigator.clipboard?.writeText(txt);
    setSnack({ message: "Copied!" });
  };

  const goDetail = (u_id) => {
    navigate(`/internal/accounts/list/${encodeURIComponent(u_id)}`);
  };

  const applySavedView = (view) => {
    const nextFilters = { ...defaultFilters(), ...view.filters };
    if (nextFilters.subscription_status === "trial") {
      nextFilters.subscription_status = "trialing";
    }
    updateFilters(nextFilters);
    setSaveViewAnchor(null);
    setSnack({ message: `Loaded “${view.name}”` });
  };

  const handleDeleteView = (viewName) => {
    persistViews(savedViews.filter((view) => view.name !== viewName));
    setSnack({ message: `Deleted “${viewName}”` });
  };

  const handleSaveView = () => {
    const name = saveViewName.trim();
    if (!name) return;
    const entry = {
      name,
      filters: { ...filters },
    };
    persistViews(
      [...savedViews.filter((v) => v.name !== name), entry].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    );
    setSaveDialogOpen(false);
    setSaveViewName("");
    setSnack({ message: "Saved filter view" });
  };

  const handleBulkExport = async () => {
    setSnack({ message: "Export started" });
    const subset = rows.filter((r) => selected.has(r.u_id));
    const blob = new Blob([rowsToCsv(subset)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `accounts_export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ message: "Export completed" });
  };

  const handleExportOrganizationsOverview = async () => {
    setExporting(true);
    const res = await accountServices.getOrganizationsOverview();
    const overview = res.data;

    if (!overview) {
      setSnack({
        message: "Data missing. Please check the db for confirmation.",
      });
      return;
    }

    const blob = new Blob([orgsToCsv(overview)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `organizations_overview_export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ message: "Export started" });
    setExporting(false);
  };

  const getRiskActionTargets = () => {
    if (selectedRows.length === 0) {
      setSnack({ message: "Select at least one account first." });
      return null;
    }
    if (selectedRows.length > RISK_ACTION_LIMIT) {
      setSnack({
        message: `Select up to ${RISK_ACTION_LIMIT} accounts for resend/reset actions.`,
      });
      return null;
    }
    return selectedRows;
  };

  const openReasonDialog = (type, title) => {
    const targets = getRiskActionTargets();
    if (!targets) return;
    setReasonActionType(type);
    setReasonDialogTitle(title);
    setReasonInput("");
    setReasonDialogOpen(true);
  };

  const submitReasonAction = async () => {
    const reason = reasonInput.trim();
    if (!reason) {
      setSnack({ message: "Reason is required." });
      return;
    }
    const targets = getRiskActionTargets();
    if (!targets) return;

    const actionKey =
      reasonActionType === "resend" ? "resendVerification" : "reset2fa";

    setReasonDialogOpen(false);
    await runActionWithMiddleware({
      actionKey,
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm security check for {targets.length} account(s).
        </Typography>
      ),
      execute: async ({ verificationCode }) => {
        setBulkSubmitting(true);
        try {
          const requests = targets.map((target) => {
            if (reasonActionType === "resend") {
              return accountServices.resendAccountVerification(target.u_id, {
                reason,
                verificationCode,
              });
            }
            if (reasonActionType === "reset2fa") {
              return accountServices.resetAccount2fa(target.u_id, {
                reason,
                verificationCode,
              });
            }
            return Promise.resolve();
          });

          const results = await Promise.allSettled(requests);
          const success = results.filter(
            (r) => r.status === "fulfilled"
          ).length;
          const failed = results.length - success;
          setSnack({
            message: failed
              ? `${success} succeeded, ${failed} failed.`
              : `${success} account(s) processed successfully.`,
          });
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  };

  const openResetPasswordDialog = () => {
    const targets = getRiskActionTargets();
    if (!targets) return;
    setResetPasswordValue("");
    setResetPasswordReason("");
    setResetPasswordDialogOpen(true);
  };

  const submitResetPassword = async () => {
    const reason = resetPasswordReason.trim();
    if (resetPasswordValue.length < 8) {
      setSnack({ message: "New password must be at least 8 characters." });
      return;
    }
    if (!reason) {
      setSnack({ message: "Reason is required." });
      return;
    }
    const targets = getRiskActionTargets();
    if (!targets) return;

    setResetPasswordDialogOpen(false);
    await runActionWithMiddleware({
      actionKey: "resetPassword",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm security check for password reset on {targets.length}{" "}
          account(s).
        </Typography>
      ),
      execute: async ({ verificationCode }) => {
        setBulkSubmitting(true);
        try {
          const results = await Promise.allSettled(
            targets.map((target) =>
              accountServices.resetAccountPassword(target.u_id, {
                newPassword: resetPasswordValue,
                reason,
                verificationCode,
              })
            )
          );
          const success = results.filter(
            (r) => r.status === "fulfilled"
          ).length;
          const failed = results.length - success;
          setSnack({
            message: failed
              ? `${success} succeeded, ${failed} failed.`
              : `Password reset successfully for ${success} account(s).`,
          });
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  };

  const requestDeleteAccount = (event, row) => {
    event.stopPropagation();
    setDeleteTargetRow(row);
    setDeleteReasonInput("");
    setDeleteReasonDialogOpen(true);
  };

  const confirmDeleteAccount = async () => {
    const reason = deleteReasonInput.trim();
    if (!reason) {
      setSnack({ message: "Reason is required." });
      return;
    }
    if (!deleteTargetRow?.u_id) return;

    setDeleteReasonDialogOpen(false);
    await runActionWithMiddleware({
      actionKey: "deleteAccount",
      details: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
            border: "1px dashed",
            borderColor: "warning.main",
            borderRadius: 2,
            p: 2,
            width: "100%",
          }}
        >
          <Typography
            variant='caption'
            sx={{ color: "warning.dark", fontWeight: 600, mb: 0.5 }}
          >
            TARGET TO DELETE ACCOUNT
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "text.primary",
              fontWeight: 700,
              fontSize: "1.05rem",
              wordBreak: "break-all",
            }}
          >
            <CrisisAlertIcon
              sx={{
                color: theme.palette.warning.main,
                fontSize: 22,
                mr: 0.5,
              }}
            />
            {deleteTargetRow.email || deleteTargetRow.u_id}
          </Box>
        </Box>
      ),
      execute: async () => {
        setBulkSubmitting(true);
        try {
          await accountServices.deleteAccount(deleteTargetRow.u_id, {
            reason,
          });
          setSnack({
            message: `Account deleted: ${
              deleteTargetRow.email || deleteTargetRow.u_id
            }`,
          });
          setDeleteTargetRow(null);
          await fetchAccounts();
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to delete account";
          setSnack({ message });
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  };

  const requestAddNewUser = (event, row) => {
    event.stopPropagation();
    if (!row?.u_id) return;
    setInviteAdminRow(row);
    setInviteKind("subsidiary");
    setInviteName("");
    setInviteEmail("");
    setInvitePhoneCode("+65");
    setInvitePhoneNumber("");
    setInviteAddress("");
    setInvitePassword(generateInitialPassword());
    setInviteRole("viewer");
    setInviteExpireDate("");
    setInviteReason("");
    setInviteDialogOpen(true);
  };

  const confirmInviteMember = async () => {
    const adminId = inviteAdminRow?.u_id;
    if (!adminId) return;

    const reason = inviteReason.trim();
    if (!reason) {
      setSnack({ message: "Reason is required." });
      return;
    }

    if (inviteKind === "subsidiary") {
      let expire_date;
      if (inviteExpireDate && !isNaN(Number(inviteExpireDate))) {
        const days = Number(inviteExpireDate);
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + days);
        expire_date = date.toISOString().slice(0, 10); // yyyy-mm-dd
      } else {
        expire_date = undefined;
      }
      const body = {
        name: inviteName || "Subsidiary User",
        email: inviteEmail,
        phoneCode: invitePhoneCode,
        phoneNumber: invitePhoneNumber,
        address: inviteAddress,
        password: invitePassword || generateInitialPassword(),
        role: inviteRole || "viewer",
        expire_date,
        reason,
      };

      if (!body.email?.trim()) {
        setSnack({ message: "Email is required for Subsidiary." });
        return;
      }

      setInviteDialogOpen(false);
      setBulkSubmitting(true);
      try {
        await accountServices.inviteSubsidiary(adminId, body);
        setSnack({ message: "Subsidiary invite sent." });
        await fetchAccounts();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to invite subsidiary";
        setSnack({ message });
      } finally {
        setBulkSubmitting(false);
      }
      return;
    }

    const body = {
      name: inviteName || "",
      email: inviteEmail,
      role: inviteRole || "editor",
      reason,
    };

    if (!body.name?.trim()) {
      setSnack({ message: "Name is required for Sub-user." });
      return;
    }

    if (!body.email?.trim()) {
      setSnack({ message: "Email is required for Sub-user." });
      return;
    }

    setInviteDialogOpen(false);
    setBulkSubmitting(true);
    try {
      await accountServices.inviteSubUser(adminId, body);
      setSnack({ message: "Sub-user invite sent." });
      await fetchAccounts();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to invite sub-user";
      setSnack({ message });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const requestConvertAccountType = (event, row) => {
    event.stopPropagation();
    if (!row?.u_id) return;
    const userType = row?.user_auth?.user_type;
    if (!["user", "subsidiary"].includes(userType)) return;

    setConvertTargetRow(row);
    setConvertReason("");
    setConvertDialogOpen(true);
  };

  const confirmConvertAccountType = async () => {
    const target = convertTargetRow;
    if (!target?.u_id) return;

    const reason = convertReason.trim();
    if (!reason) {
      setSnack({ message: "Reason is required." });
      return;
    }

    const currentType = target?.user_auth?.user_type;
    const isSubsidiary = currentType === "subsidiary";

    if (!isSubsidiary && currentType !== "user") {
      setSnack({ message: "Only user/subsidiary accounts can be converted." });
      return;
    }

    setConvertDialogOpen(false);
    await runActionWithMiddleware({
      actionKey: "convertAccountType",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm conversion for {target.email || target.u_id} from{" "}
          {formatUserType(currentType)} to{" "}
          {isSubsidiary ? formatUserType("user") : formatUserType("subsidiary")}
          .
        </Typography>
      ),
      execute: async ({ verificationCode }) => {
        setBulkSubmitting(true);
        try {
          if (isSubsidiary) {
            await accountServices.convertSubsidiaryToUser(target.u_id, {
              reason,
              verificationCode,
            });
          } else {
            // Calculate expiration date if convertExpireDays is provided
            let expiresAt;
            if (convertExpireDays && !isNaN(convertExpireDays)) {
              const days = Number(convertExpireDays);
              expiresAt = dayjs().add(days, "day").toISOString();
            }

            await accountServices.convertSubUserToSubsidiary(target.u_id, {
              reason,
              verificationCode,
              expire_date: expiresAt,
            });
          }
          setSnack({
            message: isSubsidiary
              ? "Converted subsidiary to user."
              : "Converted sub-user to subsidiary.",
          });
          setConvertTargetRow(null);
          await fetchAccounts();
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to convert account type";
          setSnack({ message });
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <AccountsFilters
            filters={filters}
            onChange={updateFilters}
            onReset={() => updateFilters(defaultFilters())}
            userTypeOptions={USER_TYPE_OPTIONS}
            userRoleOptions={USER_ROLE_OPTIONS}
            userStatusOptions={USER_STATUS_OPTIONS}
            onSavedViews={(event) => setSaveViewAnchor(event.currentTarget)}
            savedViewsOpen={Boolean(saveViewAnchor)}
            onSaveView={() => setSaveDialogOpen(true)}
            onRefresh={handleRefresh}
            loading={loading}
            onExport={handleExportOrganizationsOverview}
            exporting={exporting}
          />

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
              border: 1,
              borderTop: 0,
              borderColor: "divider",
              borderRadius: "0 0 12px 12px",
              bgcolor: "background.paper",
            }}
          >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: "auto",
                overscrollBehavior: "contain",
                "& .MuiTable-root": {
                  minWidth: "max-content",
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding='checkbox'
                      sx={{
                        ...accountsHeadCellSx,
                        ...stickyColBaseSx,
                        zIndex: 4,
                      }}
                    >
                      <Checkbox
                        indeterminate={somePageSelected}
                        checked={allPageSelected}
                        onChange={handleSelectAll}
                        size='small'
                      />
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === "name" ? order : false}
                      sx={{
                        ...accountsHeadCellSx,
                        ...stickyColSecondSx,
                        zIndex: 4,
                      }}
                    >
                      <TableSortLabel
                        sx={accountsSortLabelSx}
                        active={orderBy === "name"}
                        direction={orderBy === "name" ? order : "asc"}
                        onClick={() => handleSort("name")}
                      >
                        Account
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={accountsHeadCellSx}>Phone number</TableCell>
                    <TableCell
                      sortDirection={orderBy === "created_time" ? order : false}
                      sx={accountsHeadCellSx}
                    >
                      <TableSortLabel
                        sx={accountsSortLabelSx}
                        active={orderBy === "created_time"}
                        direction={orderBy === "created_time" ? order : "desc"}
                        onClick={() => handleSort("created_time")}
                      >
                        Account created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === "status" ? order : false}
                      sx={accountsHeadCellSx}
                    >
                      <TableSortLabel
                        sx={accountsSortLabelSx}
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleSort("status")}
                      >
                        Account status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === "user_type" ? order : false}
                      sx={accountsHeadCellSx}
                    >
                      <TableSortLabel
                        sx={accountsSortLabelSx}
                        active={orderBy === "user_type"}
                        direction={orderBy === "user_type" ? order : "asc"}
                        onClick={() => handleSort("user_type")}
                      >
                        Account type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === "user_role" ? order : false}
                      sx={accountsHeadCellSx}
                    >
                      <TableSortLabel
                        sx={accountsSortLabelSx}
                        active={orderBy === "user_role"}
                        direction={orderBy === "user_role" ? order : "asc"}
                        onClick={() => handleSort("user_role")}
                      >
                        Access role
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={accountsHeadCellSx}>
                      Subscription plan
                    </TableCell>
                    <TableCell sx={accountsHeadCellSx}>Promo codes</TableCell>
                    <TableCell sx={accountsHeadCellSx}>
                      Subscription status
                    </TableCell>
                    <TableCell sx={accountsHeadCellSx}>
                      Next billing date
                    </TableCell>
                    <TableCell sx={accountsHeadCellSx}>
                      Trial end date
                    </TableCell>
                    <TableCell align='center' sx={accountsHeadCellSx}>
                      Payment method verification
                    </TableCell>
                    <TableCell sx={accountsHeadCellSx}>Last login</TableCell>
                    <TableCell align='right' sx={accountsHeadCellSx}>
                      Account actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={15} sx={{ p: 0, border: "none" }}>
                        {loading ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: 180,
                              width: "100%",
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              m: 2,
                              p: 4,
                              textAlign: "center",
                              bgcolor: (t) =>
                                alpha(t.palette.primary.main, 0.02),
                              border: "1px dashed",
                              borderColor: (t) =>
                                alpha(t.palette.primary.main, 0.2),
                              borderRadius: 2,
                            }}
                          >
                            <Typography
                              variant='body1'
                              color='text.secondary'
                              gutterBottom
                              sx={{ mb: 1 }}
                            >
                              No accounts match these filters.
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              Try clearing filters or widening your search.
                            </Typography>
                            <Button
                              sx={{ mt: 2, textTransform: "none" }}
                              variant='outlined'
                              onClick={() => updateFilters(defaultFilters())}
                            >
                              Clear filters
                            </Button>
                          </Paper>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((row) => {
                      const st = STATUS_CHIP[row.user_auth?.status] || {
                        label: row.user_auth?.status || "—",
                        color: "default",
                      };
                      const userType = formatUserType(
                        row.user_auth?.user_type || "—"
                      );
                      const userRole = row.user_auth?.user_role || "—";
                      const phone = [row.phone_code, row.phone_number]
                        .filter(Boolean)
                        .join(" ");
                      const hitpayVerified =
                        row.subscription?.verified === true;
                      const subStatus = row.subscription?.status
                        ? row.subscription.status.charAt(0).toUpperCase() +
                          row.subscription.status.slice(1)
                        : "—";

                      const planName = row.subscription?.plan?.name || "—";
                      const nextBilling = row.subscription?.next_billing_date
                        ? new Date(
                            row.subscription.next_billing_date
                          ).toLocaleDateString()
                        : "—";
                      const trialEnd =
                        subStatus === "Trialing"
                          ? row.subscription?.trial_end
                            ? new Date(
                                row.subscription.trial_end
                              ).toLocaleDateString()
                            : "—"
                          : "NA";
                      const lastLoginAt = row.user_auth?.last_login_at
                        ? new Date(row.user_auth.last_login_at).toLocaleString()
                        : "—";
                      return (
                        <TableRow
                          key={row.u_id}
                          selected={selected.has(row.u_id)}
                          onClick={() => goDetail(row.u_id)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: (t) =>
                                alpha(t.palette.primary.main, 0.02),
                            },
                          }}
                        >
                          <TableCell
                            padding='checkbox'
                            onClick={(e) => e.stopPropagation()}
                            sx={{ ...accountsBodyCellSx, ...stickyColBaseSx }}
                          >
                            <Checkbox
                              checked={selected.has(row.u_id)}
                              onChange={() => toggleOne(row.u_id)}
                              size='small'
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              ...accountsBodyCellSx,
                              ...stickyColSecondSx,
                              bgcolor: selected.has(row.u_id)
                                ? "#f3f0f7"
                                : "background.paper",
                            }}
                          >
                            <Stack
                              direction='row'
                              spacing={1.5}
                              alignItems='center'
                            >
                              <UserAvatar name={row.name} email={row.email} />
                              <Box sx={{ minWidth: 0 }}>
                                {/* 第一行：用户名 + 机构名标签 */}
                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={1}
                                  sx={{ mb: 0.5 }}
                                >
                                  <Typography
                                    variant='body2'
                                    fontWeight={700}
                                    noWrap
                                  >
                                    {row.name}
                                  </Typography>
                                  {row.subscription?.organization?.name && (
                                    <Chip
                                      label={row.subscription.organization.name}
                                      size='small'
                                      color='primary'
                                      variant='outlined'
                                      sx={{
                                        height: 20,
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        minWidth: 0,
                                      }}
                                    />
                                  )}
                                </Stack>

                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={0.5}
                                >
                                  <Typography
                                    variant='caption'
                                    color='text.disabled'
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {row.email}
                                  </Typography>
                                  <Tooltip title='Copy email'>
                                    <IconButton
                                      size='small'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyTxt(row.email);
                                      }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>

                                {/* 第三行：UID + 复制按钮 */}
                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={0.5}
                                >
                                  <Typography
                                    variant='caption'
                                    color='text.disabled'
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {row.u_id}
                                  </Typography>
                                  <IconButton
                                    size='small'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyTxt(row.u_id);
                                    }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Stack>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {phone || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {row.created_time
                                ? new Date(
                                    row.created_time
                                  ).toLocaleDateString()
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Chip
                              size='small'
                              label={st.label}
                              color={st.color}
                              variant={
                                st.color === "default" ? "outlined" : "filled"
                              }
                            />
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {userType}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {userRole}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {planName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <AccountPromoCell account={row} />
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {subStatus}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {safeDate(nextBilling)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {safeDate(trialEnd)}
                            </Typography>
                          </TableCell>
                          <TableCell align='center' sx={accountsBodyCellSx}>
                            <Chip
                              size='small'
                              label={hitpayVerified ? "Verified" : "Unverified"}
                              color={hitpayVerified ? "success" : "error"}
                              variant='filled'
                            />
                          </TableCell>
                          <TableCell sx={accountsBodyCellSx}>
                            <Typography variant='body2' color='text.secondary'>
                              {safeDateTime(lastLoginAt)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align='right'
                            onClick={(e) => e.stopPropagation()}
                            sx={accountsBodyCellSx}
                          >
                            <Tooltip title='Detail'>
                              <IconButton
                                size='small'
                                onClick={() => goDetail(row.u_id)}
                              >
                                <VisibilityIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                            {["user", "subsidiary"].includes(
                              row.user_auth?.user_type
                            ) && (
                              <Tooltip title='Convert account to Sub-user/Subsidiary'>
                                <IconButton
                                  size='small'
                                  color='info'
                                  onClick={(e) =>
                                    requestConvertAccountType(e, row)
                                  }
                                >
                                  <ChangeCircleIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}
                            {row.user_auth?.user_type === "admin" && (
                              <Tooltip title='Add users to organization'>
                                <IconButton
                                  size='small'
                                  color='success'
                                  onClick={(e) => requestAddNewUser(e, row)}
                                >
                                  <GroupAddIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}
                            {row.user_auth?.user_type !== "admin" && (
                              <Tooltip title='Delete account'>
                                <IconButton
                                  size='small'
                                  color='error'
                                  onClick={(e) => requestDeleteAccount(e, row)}
                                >
                                  <DeleteOutlineIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: (t) => alpha(t.palette.divider, 0.1),
                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                px: { xs: 1, sm: 2 },
                py: 0.5,
                flexShrink: 0,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* <Stack direction='row' spacing={3}>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Matching
                  </Typography>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {stats.total}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Active
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: "success.main" }}
                  >
                    {stats.active}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Banned
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {stats.banned}
                  </Typography>
                </Box>
              </Stack> */}
              <TablePagination
                component='div'
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                sx={{
                  ml: "auto",
                  width: "100%",
                  minWidth: 0,
                  "& .MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    px: { xs: 0, sm: 2 },
                    columnGap: 1,
                    rowGap: 0.5,
                  },
                  "& .MuiTablePagination-spacer": {
                    display: { xs: "none", sm: "block" },
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      my: 0.5,
                    },
                  "& .MuiTablePagination-input": {
                    ml: 0,
                    mr: { xs: 1, sm: 3 },
                  },
                  "& .MuiTablePagination-actions": {
                    ml: { xs: "auto", sm: 2 },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>

        <ProcessingDialog loading={bulkSubmitting} />
        <SavedViewsMenu
          saveViewAnchor={saveViewAnchor}
          setSaveViewAnchor={setSaveViewAnchor}
          savedViews={savedViews}
          currentViewName={null}
          applySavedView={applySavedView}
          deleteSavedView={handleDeleteView}
        />

        <GenericDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          title='Save filter view'
          confirmText='Save'
          onConfirm={handleSaveView}
          content={
            <TextField
              autoFocus
              margin='dense'
              label='Name'
              fullWidth
              value={saveViewName}
              onChange={(e) => setSaveViewName(e.target.value)}
              placeholder='e.g. High-risk unverified'
            />
          }
        />

        <SecurityGuard {...guardProps} />

        <ReasonDialog
          open={deleteReasonDialogOpen}
          onClose={() => {
            setDeleteReasonDialogOpen(false);
            setDeleteTargetRow(null);
          }}
          title='Delete Account'
          subtitle='Provide a reason before security verification.'
          confirmText='Continue'
          onConfirm={confirmDeleteAccount}
          loading={bulkSubmitting}
          fields={[
            {
              key: "reason",
              label: "Reason",
              value: deleteReasonInput,
              onChange: setDeleteReasonInput,
              placeholder: "Support ticket TS-1234: ...",
              multiline: true,
              minRows: 3,
              autoFocus: true,
            },
          ]}
        />

        <ReasonDialog
          open={reasonDialogOpen}
          onClose={() => setReasonDialogOpen(false)}
          title={reasonDialogTitle}
          subtitle={`Maximum ${RISK_ACTION_LIMIT} accounts per request`}
          onConfirm={submitReasonAction}
          loading={bulkSubmitting}
          fields={[
            {
              key: "reason",
              label: "Reason",
              value: reasonInput,
              onChange: setReasonInput,
              placeholder: "Support ticket TS-1234: ...",
              multiline: true,
              minRows: 3,
              autoFocus: true,
            },
          ]}
        />

        <ReasonDialog
          open={resetPasswordDialogOpen}
          onClose={() => setResetPasswordDialogOpen(false)}
          title='Reset Password'
          subtitle={`Shared password for up to ${RISK_ACTION_LIMIT} selected accounts`}
          onConfirm={submitResetPassword}
          loading={bulkSubmitting}
          fields={[
            {
              key: "newPassword",
              label: "New password",
              type: "text",
              value: resetPasswordValue,
              onChange: setResetPasswordValue,
              placeholder: "At least 8 characters",
              autoFocus: true,
            },
            {
              key: "reason",
              label: "Reason",
              value: resetPasswordReason,
              onChange: setResetPasswordReason,
              placeholder: "Support ticket TS-1234: account recovery",
              multiline: true,
              minRows: 3,
            },
          ]}
        />

        <ReasonDialog
          open={inviteDialogOpen}
          onClose={() => {
            setInviteDialogOpen(false);
            setInviteAdminRow(null);
          }}
          title='Add users to organization'
          subtitle={
            inviteAdminRow?.email
              ? `Admin: ${inviteAdminRow.email}`
              : inviteAdminRow?.u_id
                ? `Admin: ${inviteAdminRow.u_id}`
                : undefined
          }
          onConfirm={confirmInviteMember}
          loading={bulkSubmitting}
          choice={{
            label: "Create",
            value: inviteKind,
            onChange: setInviteKind,
            options: [
              { value: "subsidiary", label: "Subsidiary" },
              { value: "sub-user", label: "Sub-user" },
            ],
          }}
          fields={
            inviteKind === "subsidiary"
              ? [
                  {
                    key: "name",
                    label: "Name",
                    value: inviteName,
                    onChange: setInviteName,
                    placeholder: "Subsidiary User",
                    autoFocus: true,
                  },
                  {
                    key: "email",
                    label: "Email",
                    value: inviteEmail,
                    onChange: setInviteEmail,
                    placeholder: "subsidiary@example.com",
                  },
                  {
                    key: "phoneCode",
                    label: "Phone code",
                    type: "select",
                    value: invitePhoneCode,
                    onChange: setInvitePhoneCode,
                    options: PHONE_CODE_OPTIONS,
                    SelectProps: { MenuProps: selectMenuProps },
                  },
                  {
                    key: "phoneNumber",
                    label: "Phone number",
                    value: invitePhoneNumber,
                    onChange: setInvitePhoneNumber,
                    placeholder: "88888888",
                  },
                  {
                    key: "address",
                    label: "Address",
                    value: inviteAddress,
                    onChange: setInviteAddress,
                    placeholder: "Singapore",
                  },
                  {
                    key: "password",
                    label: "Initial password",
                    type: "text",
                    value: invitePassword,
                    onChange: setInvitePassword,
                    placeholder: "initial-password",
                    helperText:
                      "Generate a strong password or enter one manually.",
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <Button
                            size='small'
                            variant='text'
                            startIcon={<AutoAwesomeIcon fontSize='small' />}
                            onClick={handleGenerateInvitePassword}
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                          >
                            Generate
                          </Button>
                        </InputAdornment>
                      ),
                    },
                  },
                  {
                    key: "role",
                    label: "Role",
                    type: "select",
                    value: inviteRole,
                    onChange: setInviteRole,
                    options: USER_ROLE_OPTIONS,
                  },
                  {
                    key: "expire_date",
                    label: "Account Validity (Days from today)",
                    type: "duration-days",
                    value: inviteExpireDate,
                    onChange: setInviteExpireDate,
                    placeholder: "e.g. 90",
                  },
                  {
                    key: "reason",
                    label: "Reason",
                    value: inviteReason,
                    onChange: setInviteReason,
                    multiline: true,
                    minRows: 3,
                  },
                ]
              : [
                  {
                    key: "name",
                    label: "Name",
                    value: inviteName,
                    onChange: setInviteName,
                    placeholder: "Jane Updated",
                    autoFocus: true,
                  },
                  {
                    key: "email",
                    label: "Email",
                    value: inviteEmail,
                    onChange: setInviteEmail,
                    placeholder: "subuser@example.com",
                  },
                  {
                    key: "role",
                    label: "Role",
                    type: "select",
                    value: inviteRole,
                    onChange: setInviteRole,
                    options: USER_ROLE_OPTIONS,
                  },
                  {
                    key: "reason",
                    label: "Reason",
                    value: inviteReason,
                    onChange: setInviteReason,
                    multiline: true,
                    minRows: 3,
                  },
                ]
          }
        />

        <ReasonDialog
          open={convertDialogOpen}
          onClose={() => {
            setConvertDialogOpen(false);
            setConvertTargetRow(null);
          }}
          title='Convert account type'
          subtitle={
            convertTargetRow
              ? `${convertTargetRow.email || convertTargetRow.u_id} (${formatUserType(
                  convertTargetRow.user_auth?.user_type
                )} to ${convertTargetRow.user_auth?.user_type === "user" ? "Subsidiary" : "Sub User"})`
              : undefined
          }
          onConfirm={confirmConvertAccountType}
          loading={bulkSubmitting}
          choice={
            convertTargetRow?.user_auth?.user_type === "user"
              ? {
                  label: "Expiration Days",
                  value: convertExpireDays,
                  onChange: setConvertExpireDays,
                  options: [
                    { value: 15, label: "15 days" },
                    { value: 30, label: "30 days" },
                    { value: 60, label: "60 days" },
                  ],
                }
              : null
          }
          fields={[
            {
              key: "reason",
              label: "Reason",
              value: convertReason,
              onChange: setConvertReason,
              multiline: true,
              minRows: 3,
              autoFocus: true,
            },
          ]}
        />

        <Snackbar
          open={Boolean(snack)}
          autoHideDuration={2800}
          onClose={() => setSnack(null)}
          message={snack?.message}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            top: 24,
            "& .MuiSnackbarContent-root": {
              backgroundColor: "#603F97",
              borderRadius: 4,
              boxShadow: "0 8px 24px rgba(96, 63, 151, 0.25)",
              padding: "10px 24px",
              fontWeight: 500,
              fontSize: "0.95rem",
              "@keyframes slideDownFade": {
                "0%": { opacity: 0, transform: "translateY(-20px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
              animation: "slideDownFade 0.3s ease-out",
            },
          }}
        />

        {selectedCount > 0 && (
          <Box
            sx={{
              position: "fixed",
              left: { xs: 16, md: 96 },
              right: 16,
              bottom: 24,
              zIndex: (t) => t.zIndex.snackbar,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={0}
              role='region'
              aria-label='Selected account actions'
              sx={{
                pointerEvents: "auto",
                px: { xs: 1.5, sm: 2 },
                py: 1.75,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                ...paperBorderSx,
                maxWidth: "100%",
                boxSizing: "border-box",
                borderColor: "primary.dark",
                boxShadow: (t) =>
                  `0 8px 28px ${alpha(t.palette.primary.dark, 0.35)}`,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                backgroundImage: "none",
              }}
            >
              <Stack spacing={0.75} sx={{ flexShrink: 0 }}>
                <Chip
                  size='small'
                  label={`${selectedCount} selected`}
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: "primary.contrastText",
                    color: "primary.main",
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Stack
                direction='row'
                spacing={1}
                useFlexGap
                flexWrap='wrap'
                sx={{
                  minWidth: 0,
                  "& .MuiButton-root": {
                    minHeight: 40,
                    px: 1.5,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    "& .MuiButton-startIcon": { color: "inherit" },
                    "&.Mui-focusVisible": {
                      outline: "2px solid",
                      outlineColor: "primary.contrastText",
                      outlineOffset: 3,
                    },
                  },
                  "& .MuiButton-outlined": {
                    bgcolor: "common.white",
                    color: "primary.dark",
                    borderColor: "common.white",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.12)",
                    "&:hover": {
                      bgcolor: "#F3EFFA",
                      color: "primary.dark",
                      borderColor: "#F3EFFA",
                    },
                    "&:active": {
                      bgcolor: "#E6DDF2",
                      borderColor: "#E6DDF2",
                      boxShadow: "none",
                    },
                  },
                  "& .MuiButton-text": {
                    color: "primary.contrastText",
                    border: "1px solid",
                    borderColor: (t) =>
                      alpha(t.palette.primary.contrastText, 0.6),
                    "&:hover": {
                      color: "primary.contrastText",
                      bgcolor: "primary.dark",
                      borderColor: "primary.contrastText",
                    },
                  },
                }}
              >
                <Button
                  size='small'
                  variant='outlined'
                  color='inherit'
                  startIcon={<EmailIcon />}
                  onClick={() =>
                    openReasonDialog(
                      "resend",
                      "Resend Verification / Invitation Email"
                    )
                  }
                  sx={{ textTransform: "none" }}
                >
                  Resend Verification/Invitation
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  color='inherit'
                  startIcon={<PasswordIcon />}
                  onClick={openResetPasswordDialog}
                  sx={{ textTransform: "none" }}
                >
                  Reset Password
                </Button>
                <Button
                  startIcon={<GoogleIcon />}
                  size='small'
                  variant='outlined'
                  color='inherit'
                  onClick={() => openReasonDialog("reset2fa", "Reset 2FA")}
                  sx={{ textTransform: "none" }}
                >
                  Reset 2FA
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  color='inherit'
                  startIcon={<FileDownloadIcon />}
                  onClick={handleBulkExport}
                  sx={{ textTransform: "none" }}
                >
                  Export CSV
                </Button>
                <Button
                  size='small'
                  color='inherit'
                  onClick={() => setSelected(new Set())}
                  sx={{ textTransform: "none" }}
                >
                  Clear
                </Button>
              </Stack>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

function UserAvatar({ name, email }) {
  const letter = (name && name[0]) || (email && email[0]) || "?";
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
        color: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.875rem",
        fontWeight: 600,
        flexShrink: 0,
        border: "1px solid",
        borderColor: (t) => alpha(t.palette.primary.main, 0.22),
      }}
    >
      {letter.toUpperCase()}
    </Box>
  );
}

export default AccountsList;
